// Welle M1b: Absender-Erkennung im Mail-Bereich — Badges (Liste), Avatar-
// Variante, Absender-Karte (Detail) und das Sheet "Absender speichern /
// Kontakt bearbeiten". Daten kommen aus M1a (mail.absender, prio_effektiv,
// prio_quelle). Priorität wird NUR aus prio_effektiv/prio_quelle gelesen.
import React, { useEffect, useState } from 'react';
import { MIcon } from '../../lib/micon';
import { LdSheet } from '../../components/ui/LdSheet';
import { LdSheetHeader, LdField, LdInputField, LdChipGroup, LdChip, LdSheetSaveBar } from '../../components/ld/LdForm';
import { Switch } from '../../components/ld/Switch';
import { apiPost, apiPut, apiDelete, zeigeApiFehler } from '../../lib/api';
import { confirm } from '../../lib/confirm';
import { toast } from '../../lib/toast';
import './MailAbsender.css';

export type Kind = { id: number; vorname: string; nachname: string; klasse: string };
export type KontaktInfo = { id: number; name: string | null; rolle: string | null; prio: 0 | 1 };
export type Absender = { typ: 'eltern' | 'kontakt' | 'beides' | null; kinder: Kind[]; kontakt: KontaktInfo | null; hauptkontakt?: boolean };
export type Kontakt = { id: number; email: string; name: string | null; rolle: string | null; prio: 0 | 1 };

export const ROLLEN: { value: string; label: string }[] = [
  { value: 'schulleitung', label: 'Schulleitung' },
  { value: 'sekretariat',  label: 'Sekretariat' },
  { value: 'kollegium',    label: 'Kollegium' },
  { value: 'elternbeirat', label: 'Elternbeirat' },
  { value: 'schulamt',     label: 'Schulamt' },
  { value: 'sonstiges',    label: 'Sonstiges' },
];
export const rollenLabel = (r: string | null | undefined) => ROLLEN.find(x => x.value === r)?.label ?? null;

const istPrioKontakt = (m: any) => m?.prio_quelle === 'kontakt';
// KI-Badge sparsam: nur KI-Quelle UND wirksam hoch — nie zusätzlich zum Stern.
export const istKiDringend = (m: any) => m?.prio_quelle === 'ki' && m?.prio_effektiv === 'hoch';

export function initialenVon(text: string): string {
  const t = (text || '?').replace(/@.*/, '').trim().split(/[\s._-]+/).filter(Boolean);
  return ((t.length >= 2 ? t[0][0] + t[1][0] : (t[0] ?? '?').slice(0, 2)) || '?').toUpperCase();
}

// ── Liste: Avatar-Variante je Absender-Typ ─────────────────────────────────
export function AbsenderAvatar({ mail }: { mail: any }) {
  const typ: Absender['typ'] = mail.absender?.typ ?? null;
  if (!typ) {
    return <div className="ld-mabs-avatar ld-mabs-avatar--unbekannt" aria-hidden="true"><MIcon name="person" size={16} /></div>;
  }
  const name = mail.absender?.kontakt?.name || mail.sender_name || mail.sender_email || '?';
  return (
    <div className={`ld-mabs-avatar ld-mabs-avatar--${typ === 'kontakt' ? 'kontakt' : 'eltern'}`} aria-hidden="true">
      {initialenVon(name)}
    </div>
  );
}

// ── Liste: Badges unter Betreff/Vorschau ──────────────────────────────────
export function AbsenderBadges({ mail }: { mail: any }) {
  const a: Absender | undefined = mail.absender;
  const kinder = a?.kinder ?? [];
  const rolle = rollenLabel(a?.kontakt?.rolle);
  const ki = kiHinweis(mail);
  if (kinder.length === 0 && !rolle && !ki) return null;
  return (
    <div className="ld-mabs-badges">
      {kinder.slice(0, 2).map(k => (
        <span key={k.id} className="ld-mabs-badge ld-mabs-badge--kind">
          {k.vorname} {k.nachname}, {k.klasse}
        </span>
      ))}
      {kinder.length > 2 && <span className="ld-mabs-badge ld-mabs-badge--kind">+{kinder.length - 2}</span>}
      {rolle && <span className="ld-mabs-badge ld-mabs-badge--rolle">{rolle}</span>}
      {ki && <span className="ld-mabs-badge ld-mabs-badge--ki"><MIcon name="auto_awesome" size={13} />{ki}</span>}
    </div>
  );
}

// Stern neben dem Namen (nur Prio-Kontakte)
export function PrioStern({ mail, size = 14 }: { mail: any; size?: number }) {
  if (!istPrioKontakt(mail)) return null;
  return <span className="ld-mabs-stern" title="Prio-Kontakt" aria-label="Prio-Kontakt"><MIcon name="star" size={size} fill /></span>;
}

// Welle K5: KI-Hinweis in der Liste (Amber mit Funken) — erkannte Art oder „dringend“
const KI_ART: Record<string, string> = { krankmeldung: 'Krankmeldung', termin: 'Terminanfrage', elterngespraech: 'Gesprächswunsch' };
export function kiHinweis(m: any): string | null {
  return KI_ART[m?.ai_category] ?? (istKiDringend(m) ? 'dringend' : null);
}

// ── Detail: kompakte Absender-Karte (Welle K5) — Pfeil zum Profil des Kindes ──
export function AbsenderKurz({ mail, onKind }: { mail: any; onKind: (kind: Kind) => void }) {
  const a: Absender = mail.absender ?? { typ: null, kinder: [], kontakt: null };
  const adresse: string = mail.sender_email ?? '';
  const name = a.kontakt?.name || mail.sender_name || adresse || 'Unbekannter Absender';
  const istEltern = a.typ === 'eltern' || a.typ === 'beides';
  const kind = istEltern ? a.kinder[0] : undefined;
  const zeile = istEltern && a.kinder.length
    ? `Elternteil von ${a.kinder.map(k => `${k.vorname} ${k.nachname}, ${k.klasse}`).join(' und ')}`
    : rollenLabel(a.kontakt?.rolle) ?? (a.kontakt ? adresse : adresse || '');
  const inhalt = (
    <>
      <span className={`ld-mabs-avatar ld-mabs-avatar--${!a.typ ? 'unbekannt' : istEltern ? 'eltern' : 'kontakt'}`} aria-hidden="true">{initialenVon(name)}</span>
      <span className="ld-mabs-kurz__text">
        <b>{name}{a.kontakt?.prio === 1 && <span className="ld-mabs-stern" aria-label="Prio-Kontakt"><MIcon name="star" size={14} fill /></span>}</b>
        {zeile && <small>{zeile}</small>}
      </span>
    </>
  );
  return kind ? (
    <button type="button" className="ld-mabs-kurz" onClick={() => onKind(kind)} aria-label={`${name}, Profil von ${kind.vorname} öffnen`}>
      {inhalt}<MIcon name="chevron_right" size={20} className="ld-mabs-kurz__pfeil" />
    </button>
  ) : <div className="ld-mabs-kurz">{inhalt}</div>;
}

// ── Sheet: Absender speichern / Kontakt bearbeiten ────────────────────────
// Rückfrage + Löschen; true = gelöscht. Aufrufer schließt vorher ein offenes Sheet.
export async function kontaktLoeschenMitRueckfrage(id: number): Promise<boolean> {
  const ok = await confirm('Der Kontakt wird gelöscht. Mails von dieser Adresse werden danach nicht mehr erkannt.',
    { title: 'Kontakt löschen?', confirmLabel: 'Kontakt löschen', danger: true });
  if (!ok) return false;
  try {
    await apiDelete(`/api/kontakte/${id}`);
    toast('Kontakt gelöscht', 'success');
    return true;
  } catch (e) { zeigeApiFehler(e, 'Löschen fehlgeschlagen'); return false; }
}

export function KontaktSheet({ open, onClose, vorlage, onGespeichert, onWiederOeffnen }: {
  open: boolean;
  onClose: () => void;
  /** id gesetzt → bearbeiten, sonst neu anlegen */
  vorlage: { id?: number; email: string; name: string | null; rolle: string | null; prio: 0 | 1 } | null;
  onGespeichert: () => void;
  /** Nach abgebrochener Lösch-Rückfrage das Sheet mit derselben Vorlage wieder öffnen. */
  onWiederOeffnen?: (v: { id?: number; email: string; name: string | null; rolle: string | null; prio: 0 | 1 }) => void;
}) {
  const [name, setName] = useState('');
  const [rolle, setRolle] = useState<string | null>(null);
  const [prio, setPrio] = useState(false);
  const [laden, setLaden] = useState(false);
  useEffect(() => {
    if (!open || !vorlage) return;
    setName(vorlage.name ?? ''); setRolle(vorlage.rolle ?? null); setPrio(vorlage.prio === 1);
  }, [open, vorlage]);
  if (!vorlage) return null;
  const bearbeiten = vorlage.id != null;

  async function speichern() {
    if (!vorlage) return;
    setLaden(true);
    try {
      const body = { name: name.trim() || null, rolle, prio };
      if (bearbeiten) await apiPut(`/api/kontakte/${vorlage.id}`, body);
      else await apiPost('/api/kontakte', { ...body, email: vorlage.email });
      toast(bearbeiten ? 'Kontakt gespeichert' : 'Absender gespeichert', 'success');
      onGespeichert();
      onClose();
    } catch (e) { zeigeApiFehler(e, 'Speichern fehlgeschlagen'); } finally { setLaden(false); }
  }

  // Ein offenes (modales) LdSheet blockiert den ConfirmHost — daher Sheet
  // schließen, nachfragen, bei "Abbrechen" wieder öffnen (Lektion Admin-Konsole).
  async function loeschen() {
    if (!vorlage?.id) return;
    const v = vorlage;
    onClose();
    await new Promise(r => setTimeout(r, 250));
    const ok = await kontaktLoeschenMitRueckfrage(v.id!);
    if (ok) onGespeichert();
    else onWiederOeffnen?.(v);
  }

  return (
    <LdSheet open={open} onClose={onClose} maxWidth={480} ariaLabel={bearbeiten ? 'Kontakt bearbeiten' : 'Absender speichern'}>
      <div className="ld-mabs-sheet">
        <LdSheetHeader icon={<MIcon name={bearbeiten ? 'contact_page' : 'person_add'} size={20} />}
          title={bearbeiten ? 'Kontakt bearbeiten' : 'Absender speichern'}
          subtitle={bearbeiten ? undefined : 'Dann siehst du in Zukunft sofort, wer schreibt.'}
          onClose={onClose} />
        <LdField label="Name">
          <LdInputField value={name} onChange={e => setName(e.target.value)} placeholder="z. B. Dr. Petra Vogel" maxLength={200} />
        </LdField>
        <LdField label="E-Mail">
          <LdInputField value={vorlage.email} readOnly aria-readonly="true" title="Die Adresse kommt aus der Mail und lässt sich nicht ändern." icon={<MIcon name="lock" size={16} />} />
        </LdField>
        <LdField label="Rolle">
          <LdChipGroup>
            {ROLLEN.map(r => (
              <LdChip key={r.value} active={rolle === r.value} onClick={() => setRolle(rolle === r.value ? null : r.value)}>{r.label}</LdChip>
            ))}
          </LdChipGroup>
        </LdField>
        <label className={`ld-mabs-prio${prio ? ' ld-mabs-prio--an' : ''}`}>
          <Switch checked={prio} onChange={setPrio} />
          <span className="ld-mabs-prio__text">
            <span className="ld-mabs-prio__titel"><span className="ld-mabs-stern"><MIcon name="star" size={15} fill /></span>Immer als Priorität behandeln</span>
            <span className="ld-mabs-prio__info">Steht oben und erscheint auf „Heute“ – unabhängig von der KI.</span>
          </span>
        </label>
        <LdSheetSaveBar onClick={speichern} loading={laden}>{bearbeiten ? 'Kontakt speichern' : 'Absender speichern'}</LdSheetSaveBar>
        {bearbeiten && (
          <button className="ld-mabs-loeschen" onClick={loeschen}><MIcon name="delete" size={16} />Kontakt löschen</button>
        )}
        <p className="ld-mabs-hinweis"><MIcon name="lock" size={13} />Kontakte sind nur für dich sichtbar und Teil deines Datenexports.</p>
      </div>
    </LdSheet>
  );
}
